/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * A user's roles and role bindings, for the user detail screen.
 *
 * An assignment stores only `roleBindingId`, and a binding stores only
 * `roleId` / `unitId` — no names. So the four reference lists are pulled and
 * joined here, the same way the RBAC → User Access tab does it. They are cached
 * under the same React Query keys, so opening this after that tab (or the
 * reverse) costs nothing.
 *
 * Scope is the caller's active project: a user may hold bindings in several
 * projects, and showing another project's access on this screen would
 * misrepresent what they can do here. Global bindings (appId null) apply
 * everywhere, so the backend includes them alongside the project's own.
 *
 * Read-only by design — assignments are created and revoked in one place,
 * Access Control → User Access, which this links to.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Tag } from 'sharedUi/Tag';
import { Button } from 'sharedUi/Button';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { useCanRead } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import {
  assignmentsApi,
  roleBindingsApi,
  rolesApi,
} from '../../services/rbac.service';
import { unitsService } from '../../services/units.service';
import type { RbacStatus } from '../../types/rbac';

/** One row: an assignment resolved into the names a person can read. */
interface AccessRow {
  id: string;
  role: string;
  roleKey?: string;
  unit: string;
  unitCode?: string;
  /** A binding with no unit applies across the project rather than at one unit. */
  isGlobalUnit: boolean;
  inherits: boolean;
  status?: RbacStatus;
  description?: string;
}

const Empty = ({ children }: { children: React.ReactNode }) => (
  <p className="m-0 py-6 text-center text-sm text-ink-muted">{children}</p>
);

const UserRolesCard = ({ userId }: { userId: string }) => {
  const navigate = useAppNavigate();
  const canRead = useCanRead(JOB_NAMES.USER_ROLE_PERMISSIONS);

  // The project this screen is scoped to. Undefined (not null) so the param is
  // simply omitted for a caller with no active project rather than sent empty.
  const appId = localStorage.getItem('appId') || undefined;

  const { data: assignments = [], isFetching, isError } = useQuery({
    queryKey: ['rbac', 'assignments', 'by-user', userId, appId],
    queryFn: () =>
      assignmentsApi
        .list({ user_id: userId, app_id: appId, page: 1, page_size: 200 })
        .then((r) => r.data || []),
    enabled: canRead && !!userId,
  });

  // Shared cache keys with the RBAC tabs — these are the same reference lists.
  const { data: bindings = [] } = useQuery({
    queryKey: ['rbac', 'role-bindings', 'all'],
    queryFn: () => roleBindingsApi.listAll(),
    enabled: canRead,
  });
  const { data: roles = [] } = useQuery({
    queryKey: ['rbac', 'roles', 'all'],
    queryFn: () => rolesApi.listAll(),
    enabled: canRead,
  });
  const { data: units = [] } = useQuery({
    queryKey: ['rbac', 'units', 'all'],
    queryFn: () => unitsService.getAllForDropdown(),
    enabled: canRead,
  });

  const rows: AccessRow[] = useMemo(() => {
    const bindingById = new Map(bindings.map((b: any) => [b._id, b]));
    const roleById = new Map(roles.map((r: any) => [r._id, r]));
    const unitById = new Map(units.map((u: any) => [u._id, u]));

    return assignments.map((a: any) => {
      const b: any = bindingById.get(a.roleBindingId);
      const role: any = b ? roleById.get(b.roleId) : undefined;
      const unit: any = b?.unitId ? unitById.get(b.unitId) : undefined;
      return {
        id: a._id,
        // Populated fields win; fall back to the lookup, then to the raw id so a
        // row is never blank just because a reference list was trimmed.
        role: b?.role?.displayName || b?.role?.key || role?.displayName || role?.key
          || (b ? b.roleId : a.roleBindingId),
        roleKey: b?.role?.key || role?.key,
        unit: b?.unit?.name || unit?.name || (b?.unitId ? b.unitId : ''),
        unitCode: b?.unit?.unitCode || unit?.unitCode,
        isGlobalUnit: !b?.unitId,
        inherits: !!b?.inheritChildren,
        status: a.status,
        description: a.description || b?.description,
      };
    });
  }, [assignments, bindings, roles, units]);

  return (
    <div
      className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"
      data-testid="UserView.RolesCard"
    >
      <div className="mb-2 flex items-center gap-2">
        <h2 className="m-0 text-sm font-semibold text-ink">
          Roles &amp; Access
        </h2>
        {canRead && !isFetching && rows.length > 0 && (
          <Tag value={String(rows.length)} severity="info" rounded className="text-xs" />
        )}
        <span className="ml-auto">
          <Button
            label="Manage access"
            icon="pi pi-external-link"
            variant="text"
            size="small"
            onClick={() => navigate('/access-control')}
            testId="UserView.Button.ManageAccess"
          />
        </span>
      </div>

      {!canRead && (
        <Empty>You do not have permission to view this user&rsquo;s access.</Empty>
      )}

      {canRead && isFetching && (
        <Empty>Loading access&hellip;</Empty>
      )}

      {canRead && !isFetching && isError && (
        <Empty>Could not load this user&rsquo;s access.</Empty>
      )}

      {canRead && !isFetching && !isError && rows.length === 0 && (
        <Empty>No roles assigned in this project.</Empty>
      )}

      {canRead && !isFetching && !isError && rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                {['Role', 'Bound to', 'Scope', 'Status'].map((h) => (
                  <th
                    key={h}
                    className="border-b border-gray-200 px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:text-gray-400"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-gray-100 last:border-b-0 dark:border-gray-700/60">
                  <td className="px-2 py-1.5 align-middle">
                    <span className="font-medium text-ink">{r.role}</span>
                    {r.roleKey && r.roleKey !== r.role && (
                      <span className="ml-1.5 font-mono text-[10px] text-gray-400">{r.roleKey}</span>
                    )}
                    {r.description && (
                      <span className="block text-[11px] text-gray-400">{r.description}</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 align-middle">
                    {r.isGlobalUnit ? (
                      <span className="text-ink-muted">Whole project</span>
                    ) : (
                      <>
                        <span className="text-gray-900 dark:text-gray-100">{r.unit}</span>
                        {r.unitCode && (
                          <span className="ml-1.5 font-mono text-[10px] text-gray-400">{r.unitCode}</span>
                        )}
                      </>
                    )}
                  </td>
                  <td className="px-2 py-1.5 align-middle">
                    {r.isGlobalUnit ? (
                      <span className="text-gray-400">&mdash;</span>
                    ) : r.inherits ? (
                      <Tag value="This unit and below" severity="info" className="text-xs" />
                    ) : (
                      <Tag value="This unit only" severity="secondary" className="text-xs" />
                    )}
                  </td>
                  <td className="px-2 py-1.5 align-middle">
                    <Tag
                      value={r.status || 'ACTIVE'}
                      severity={!r.status || r.status === 'ACTIVE' ? 'success' : 'warning'}
                      className="text-xs"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UserRolesCard;
